import { useEffect, useRef, useState } from 'react';
import { useAtom, useAtomValue } from 'jotai';
import qs from 'qs';
import { useLocation } from 'react-router-dom';
import { embedApi, isEmbedded, Atoms, LOGS_CONSTS, sendLogToKibana, useOnLogout } from '@cyberint/common-utils';
import { getError as defaultGetError } from '../components/embedded-messages';
import { REDIRECT_ERROR_CODE, fixFirstPath, buildEmbeddedRedirectUrl, sanitizeURL } from '../utils/embedded-utils';

const CP_REGEXP = /^https:\/\/(\S+\.)?portal\.checkpoint\.com$/;
const COR_ID_HEADER = 'x-correlation-id';
const REQ_ID_HEADER = 'x-request-id';
const INFINITY_DATA_READY_KEY = 'INFINITY_READY_DATA';

const LOGOUT_ACTIONS = {
  logout: 'logout_ack',
  app_switch: 'app_switch_ack',
  tenant_switch: 'tenant_switch_ack'
};

class EmbedInfo {
  // Guards against double-auth when the parent message and the fallback timer race.
  static startedAuth = false;
}

// eslint-disable-next-line no-console
const log = (...args) => console.log(...args);

/**
 * Framework-agnostic embedded (iframe) auth handshake with the parent (Infinity portal).
 * App-specific side effects are injected:
 *   onSignOut - clear the app's session/token state (Redux dispatch / jotai reset).
 *   basePath  - deploy base path, used to build the cross-server redirect URL.
 *   getError  - map a failed auth response to a renderable message ({ msg, origError }).
 */
export const useEmbeddedAuth = ({ onSignOut, basePath, getError = defaultGetError } = {}) => {
  const resetLoginState = useOnLogout();
  const [targetOrigin, setOrigin] = useAtom(Atoms.embeddedParentOriginState);
  const originRef = useRef(targetOrigin);
  originRef.current = targetOrigin;
  const [loading, setLoading] = useState(true);
  const [messages, setMessages] = useState([]);
  const [showDemoParams, setShowDemoParams] = useState(null);
  const [firstPath, setFirstPath] = useAtom(Atoms.firstPathState);

  const params = qs.parse(decodeURIComponent(window.location.search), { ignoreQueryPrefix: true });

  const onLogout = ackAction => {
    onSignOut?.();
    resetLoginState();
    // Infinity takes a few seconds to react (logout / navigate / tenant switch);
    // stay in the loading state so the un-auth page doesn't flash.
    setLoading(true);
    setMessages(['Logging Out ... ']);

    const data = { source: 'erm', action: ackAction };
    log('Cleaned session and tokens. About to send messages to cloud_infra:', data, 'targetOrigin:', originRef.current);

    setTimeout(() => {
      window.parent.postMessage(data, originRef.current);
      log(`window.parent.postMessage(${JSON.stringify(data)}, ${originRef.current});`);
    }, 100);
  };

  const handleFirstPath = relativePath => {
    const fixedPath = fixFirstPath(relativePath);
    log('First path (from parent message):', relativePath, 'Fixed path:', fixedPath);
    setFirstPath(fixedPath);
    return fixedPath;
  };

  useEffect(() => {
    sendLogToKibana({
      template: 'Embedded App is opening url %s',
      params: [sanitizeURL(window.location.href)]
    });

    const authenticate = async (user, fixedPath) => {
      if (EmbedInfo.startedAuth) return;
      EmbedInfo.startedAuth = true;

      const tenantId = params.tenantId || user?.activeTenant;
      setMessages(['Authenticating ... ']);

      try {
        const body = { firstTimeToken: params.firstTimeToken, tenantId, username: user?.email };
        const { data } = await embedApi.authenticateInfinityToken(body);
        const customers = data.response?.customer;
        const server = customers?.[0]?.server || data.server;

        if (server && server.toLowerCase() !== window.location.host.toLowerCase()) {
          log('Detected server url different from original host. new server:', server);
          setMessages(['Redirecting ... ']);
          const newUrl = buildEmbeddedRedirectUrl(server, fixedPath, basePath);
          log('Embedded App is redirecting', newUrl);
          sendLogToKibana({
            template: 'Embedded App is redirecting from url %s to: %s',
            params: [sanitizeURL(window.location.href), sanitizeURL(newUrl)],
            extraParams: { data }
          });
          setTimeout(() => window.location.replace(newUrl), 100);
          return;
        }

        setMessages(['Authenticated']);
        setTimeout(() => {
          setMessages([]);
          setLoading(false);
        }, 50);
      } catch (e) {
        log('Embedded authentication failed');
        const errCode = (e?.response?.data || e?.data || e)?.error_code;
        if (errCode === REDIRECT_ERROR_CODE || params?.showDemoOnError != null) {
          sendLogToKibana({
            template: 'Tenant not mapped error while loading embedded app for url: %s. showing leading/demo page',
            params: [sanitizeURL(window.location.href)],
            extraParams: { error: e },
            logLevel: LOGS_CONSTS.LOG_LEVEL_WARNING
          });
          setShowDemoParams({ user, tenantId });
          setMessages([]);
          setLoading(false);
        } else {
          const { msg, origError } = getError({ e, user, tenantId });
          const headers = e.response?.headers || {};
          setMessages([{ error: msg, origError, corId: headers[COR_ID_HEADER], reqId: headers[REQ_ID_HEADER] }]);
          setLoading(false);
          sendLogToKibana({
            template: 'Error while loading embedded app for url: %s',
            params: [sanitizeURL(window.location.href)],
            extraParams: { error: e },
            logLevel: LOGS_CONSTS.LOG_LEVEL_ERROR
          });
        }
      }
    };

    setTimeout(() => {
      if (!EmbedInfo.startedAuth) {
        log('Fallback/Delayed Authentication! (without user)');
        let user = null;
        let relativePath = null;

        try {
          let data = sessionStorage.getItem(INFINITY_DATA_READY_KEY);
          data = JSON.parse(data);
          user = data?.user;
          relativePath = data?.relativePath;
          log('Parsed INFINITY_READY_DATA with relativePath', relativePath);
          sessionStorage.removeItem(INFINITY_DATA_READY_KEY);
        } catch {
          log('Failed to parse INFINITY_READY_DATA');
        }
        const fixedPath = handleFirstPath(relativePath);
        authenticate(user, fixedPath);
      }
    }, 500);

    const onAppReady = eventData => {
      let user = eventData.user || eventData.data?.user;
      if (!user && typeof eventData === 'string') {
        try {
          user = JSON.parse(eventData).user;
        } catch {
          log('Error parsing message event data');
          return;
        }
      }
      if (!user) {
        log(`Empty User (skip auth): '${user}'`);
        return;
      }
      log('User (from parent message)');
      const relativePath = eventData.relativePath || eventData.data?.relativePath;
      const fixedPath = handleFirstPath(relativePath);
      authenticate(user, fixedPath);

      if (EmbedInfo.startedAuth) {
        setShowDemoParams(prevParams => (prevParams && !prevParams.user ? { ...prevParams, user } : prevParams));
      }
    };

    const onMessage = event => {
      if (event.origin === window.location.origin) return;
      if (event.source !== window.parent || !event.origin.match(CP_REGEXP)) return;

      setOrigin(event.origin);

      const eventData = event.data || event;
      if (eventData.source === 'cloud_infra') {
        if (eventData.action === 'frame_ready') {
          log('Argos received frame_ready event:', eventData);
          onAppReady(eventData);
          return;
        }
        const ackAction = LOGOUT_ACTIONS[eventData.action];
        if (ackAction) {
          log(`Argos received ${eventData.action} event:`, eventData);
          onLogout(ackAction);
        }
      }
    };

    window.addEventListener('message', onMessage, false);
    return () => {
      window.removeEventListener('message', onMessage);
    };
  }, []);

  return { isLoading: loading, messages, showDemoParams, firstPath };
};

export const useInteractionsEmbeddedMode = () => {
  const { pathname } = useLocation();
  const targetOrigin = useAtomValue(Atoms.embeddedParentOriginState);

  useEffect(() => {
    if (!isEmbedded) return;
    const data = { source: 'erm', action: 'url_update', data: { relativePath: pathname } };
    log('Sending url_update message:', data, 'targetOrigin:', targetOrigin);
    window.parent.postMessage(data, targetOrigin);

    const message = { source: 'erm', action: 'user_active' };
    log('Sending user_active message:', message, 'targetOrigin:', targetOrigin);
    window.parent.postMessage(message, targetOrigin);
  }, [pathname, targetOrigin]);
};
